/**
 * Dispatches Shopify's standard storefront events (https://shopify.dev/docs/api/storefront-events-and-actions/events)
 * so apps, agents and our own GTM can read what shoppers do without scraping the DOM.
 * Data comes from snippets/clnf-standard-events.liquid (#clnf-standard-events-data).
 *
 * - shopify:page:view        every page
 * - shopify:product:view     product page (<clnf-view-event> built from the standard_event_data filter)
 * - shopify:collection:view  collection page (same)
 * - shopify:cart:view        /cart page (same); B2B native drawer open; retail Upnova cart open
 * - shopify:product:select   Horizon `variant:update` on the product page
 * - shopify:cart:lines-update shopper cart changes. Retail: diffs of Upnova's cart (adds, quantity
 *                            changes, removes, wherever they happen). B2B: Horizon cart:update adds
 *                            (product form, quick add, Buy Again). Agent/app changes through
 *                            Shopify.actions.updateCart emit their own events, so they're skipped here.
 *
 * Not yet: B2B cart quantity edits and ConvertCart search (shopify:search:update).
 */
import {
  PageViewEvent,
  ProductSelectEvent,
  CartViewEvent,
  CartLinesUpdateEvent,
  createViewEventElement,
} from '@shopify/standard-events';

const AGENT_SOURCE = 'clnf-standard-actions';

/** @returns {{template?: string, view?: string | null, product?: {id: number, title: string, handle: string, options: string[]}, currency?: string}} */
function readData() {
  const node = document.getElementById('clnf-standard-events-data');
  if (!node) return {};
  try {
    return JSON.parse(node.textContent || '{}');
  } catch {
    return {};
  }
}

const data = readData();
const currency = data.currency || window.Shopify?.currency?.active || 'USD';

function root() {
  return window.Shopify?.routes?.root || '/';
}

async function fetchCart() {
  const res = await fetch(`${root()}cart.js`, { cache: 'no-store', credentials: 'same-origin' });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

function isFullCart(resource) {
  return Boolean(resource && typeof resource.item_count === 'number' && resource.token && Array.isArray(resource.items));
}

function amount(cents) {
  return ((Number(cents) || 0) / 100).toFixed(2);
}

function dispatchPageView() {
  document.dispatchEvent(
    new PageViewEvent({
      page: { template: data.template || '', title: document.title, url: window.location.href },
    })
  );
}

if (!customElements.get('clnf-view-event')) {
  customElements.define('clnf-view-event', createViewEventElement());
}

// Product / collection / cart page view: the element dispatches on connect.
function mountViewEvent() {
  if (!data.view) return;
  const main = document.getElementById('MainContent') || document.querySelector('main') || document.body;
  const element = document.createElement('clnf-view-event');
  element.hidden = true;
  element.setAttribute('view-event-trigger', 'connect');
  element.setAttribute('view-event-payload', data.view);
  main.prepend(element);
}

function onReady() {
  dispatchPageView();
  mountViewEvent();
}

// Async module: DOMContentLoaded may already have fired.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', onReady, { once: true });
} else {
  onReady();
}

/* ---------- product:select ---------- */

document.addEventListener('variant:update', (event) => {
  const product = data.product;
  const variant = event.detail?.resource;
  if (!product || !variant || !variant.id) return;
  if (event.detail?.data?.productId && String(event.detail.data.productId) !== String(product.id)) return;

  const values = Array.isArray(variant.options) ? variant.options : [];
  const selectedOptions = values
    .map((value, i) => ({ name: product.options[i] || `Option ${i + 1}`, value: String(value) }))
    .filter((option) => option.value);
  if (!selectedOptions.length) return;

  const deferred = ProductSelectEvent.createPromise();
  const target = event.target instanceof Element ? event.target : document;
  target.dispatchEvent(
    new ProductSelectEvent({
      product: { id: product.id, title: product.title, handle: product.handle },
      selectedOptions,
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    variant: {
      id: variant.id,
      title: variant.title || '',
      availableForSale: Boolean(variant.available),
      price: { amount: amount(variant.price), currencyCode: currency },
      selectedOptions,
    },
  });
});

/* ---------- cart:lines-update for the theme's own adds ---------- */

/**
 * @param {CustomEvent} event Horizon `cart:update`
 * @returns {{merchandiseId: string, quantity: number}[] | null}
 */
function addedLines(event) {
  const detail = event.detail || {};
  const info = detail.data || {};
  if (info.didError || info.source === AGENT_SOURCE) return null;

  if (info.source === 'product-form-component' && /^\d+$/.test(String(detail.sourceId || ''))) {
    return [{ merchandiseId: String(detail.sourceId), quantity: Number(info.itemCount) || 1 }];
  }
  if (Array.isArray(info.addedItems) && info.addedItems.length) {
    return info.addedItems
      .map((item) => ({
        merchandiseId: String(item.variant_id || item.id || ''),
        quantity: Number(item.quantity) || 1,
      }))
      .filter((line) => /^\d+$/.test(line.merchandiseId));
  }
  return null;
}

// Retail adds go through Upnova (it takes over the product form), so Horizon's cart:update doesn't fire
// there; retail uses the Upnova diff below instead. This bridge is for B2B (native Horizon cart).
const usesUpnova = window.CLNF_useUpnovaMobileCart === true;

document.addEventListener('cart:update', async (event) => {
  if (usesUpnova) return;
  const lines = addedLines(/** @type {CustomEvent} */ (event));
  if (!lines || !lines.length) return;

  const source = /** @type {CustomEvent} */ (event).detail?.data?.source;
  const deferred = CartLinesUpdateEvent.createPromise();
  const target = event.target instanceof Element ? event.target : document;
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: source === 'product-form-component' ? 'product' : 'dialog',
      lines,
      promise: deferred.promise,
      detail: { source: source || '' },
    })
  );

  try {
    const resource = /** @type {CustomEvent} */ (event).detail?.resource;
    const cart = isFullCart(resource) ? resource : await fetchCart();
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
  } catch {
    deferred.resolve({ cart: null });
  }
});

/* ---------- retail: cart:lines-update from Upnova cart changes ---------- */

/** @type {Map<string, {variantId: string, quantity: number}> | null} */
let snapshot = null;

function toSnapshot(cart) {
  const lines = new Map();
  for (const item of cart.items || []) {
    lines.set(String(item.key), { variantId: String(item.variant_id), quantity: Number(item.quantity) || 0 });
  }
  return lines;
}

function lineContext() {
  if (data.template === 'product') return 'product';
  if (data.template === 'cart') return 'cart';
  return 'dialog';
}

/**
 * @param {'add' | 'update' | 'remove'} action
 * @param {Array<{merchandiseId?: string, id?: string, quantity: number}>} lines
 * @param {object} cart AJAX-shaped cart
 */
function dispatchLines(action, lines, cart) {
  if (!lines.length) return;
  const deferred = CartLinesUpdateEvent.createPromise();
  const target = document.getElementById('upezCart') || document;
  target.dispatchEvent(
    new CartLinesUpdateEvent({ action, context: lineContext(), lines, promise: deferred.promise, detail: { source: 'upnova' } })
  );
  try {
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
  } catch {
    deferred.resolve({ cart: null });
  }
}

// Upnova calls back several times per change with the same cart; diffing against the last state
// turns that into one event per real change.
function onUpnovaCart(cart) {
  if (!cart || !Array.isArray(cart.items)) return;
  const next = toSnapshot(cart);
  const prev = snapshot;
  snapshot = next;
  if (!prev || Date.now() < agentPendingUntil) return;

  const adds = [];
  const updates = [];
  const removes = [];
  next.forEach((line, key) => {
    const before = prev.get(key);
    if (!before) adds.push({ merchandiseId: line.variantId, quantity: line.quantity });
    else if (line.quantity > before.quantity) adds.push({ merchandiseId: line.variantId, quantity: line.quantity - before.quantity });
    else if (line.quantity < before.quantity) updates.push({ id: key, quantity: line.quantity });
  });
  prev.forEach((_, key) => {
    if (!next.has(key)) removes.push({ id: key, quantity: 0 });
  });

  dispatchLines('add', adds, cart);
  dispatchLines('update', updates, cart);
  dispatchLines('remove', removes, cart);
}

// Agent/app changes (snippets/clnf-standard-actions.liquid) already emitted their own events: while
// one is in flight, cart changes only move the baseline; when it lands, its cart is the baseline.
let agentPendingUntil = 0;
document.addEventListener('clnf:standard-action-start', () => {
  agentPendingUntil = Date.now() + 10000;
});
document.addEventListener('clnf:standard-action-cart', (event) => {
  const cart = /** @type {CustomEvent} */ (event).detail?.cart;
  if (cart && Array.isArray(cart.items)) snapshot = toSnapshot(cart);
  agentPendingUntil = 0;
});

function watchUpnova(attempt = 0) {
  const manager = window.upez__chainEventManager;
  if (!manager || typeof manager.onCartChanged !== 'function') {
    if (attempt < 30) window.setTimeout(() => watchUpnova(attempt + 1), 500);
    return;
  }
  // Only trust Upnova's current cart as a baseline once it has loaded (has a token);
  // otherwise the first callback becomes the baseline.
  if (manager.cart && manager.cart.token && Array.isArray(manager.cart.items)) snapshot = toSnapshot(manager.cart);
  manager.onCartChanged(onUpnovaCart);
}

if (usesUpnova) watchUpnova();

/* ---------- cart:view for drawers ---------- */

let lastCartView = 0;

/** @param {Element | Document} target */
async function dispatchCartView(target) {
  const now = Date.now();
  if (now - lastCartView < 1500) return;
  lastCartView = now;
  try {
    const cart = await fetchCart();
    target.dispatchEvent(
      new CartViewEvent({ context: 'dialog', cart: CartViewEvent.createCartFromAjaxResponse(cart) })
    );
  } catch {}
}

// B2B: Horizon's drawer dispatches a non-bubbling dialog:open on <cart-drawer-component>.
document.addEventListener(
  'dialog:open',
  (event) => {
    const target = event.target;
    if (target instanceof Element && target.localName === 'cart-drawer-component') dispatchCartView(target);
  },
  true
);

// Retail: the header/bottom-nav cart link opens Upnova.
document.addEventListener(
  'click',
  (event) => {
    if (!window.CLNF_useUpnovaMobileCart) return;
    const trigger = event.target instanceof Element ? event.target.closest('[data-upez-cart]') : null;
    if (trigger) window.setTimeout(() => dispatchCartView(document.getElementById('upezCart') || document), 400);
  },
  { capture: true, passive: true }
);

// Retail: openCart from an agent/app (snippets/clnf-standard-actions.liquid calls openUpnovaCart).
document.addEventListener('clnf:upnova-open', () => {
  dispatchCartView(document.getElementById('upezCart') || document);
});
